import { TextField } from "@mui/material";
import type { FieldErrors, FieldValues, UseFormRegister } from "react-hook-form";
import { axiosInstance } from "@/providers/data";

// The server's floor for a reason (`supportAgeOverrideModel`): long enough to
// say why, so the ledger row is useful to whoever reads it later.
const MIN_REASON_LENGTH = 10;

/** Form keys of the two fields; neither goes out with the generic update. */
export const DOB_FIELD = "dateOfBirth";
export const DOB_REASON_FIELD = "dateOfBirthReason";

/**
 * Correct a user's date of birth, with a reason — saved with the rest of the
 * edit form, but NOT by the same request: the generic `PATCH /user/:id` strips
 * `dateOfBirth` on purpose (`withoutAgeGatedFields` in user.controller.ts),
 * because a birthday is age-gate state with provenance, not a plain column.
 * {@link saveDateOfBirth} posts it to `POST /api/v2/admin/users/:id/age-override`
 * instead, the one support path for it: it appends a `support_override` row to
 * the age ledger carrying the acting admin and the reason, re-derives the
 * account's age band, and — unlike the player's own write — may move the date
 * EARLIER. The reason is required only when the date changed.
 */
export function DateOfBirthFields({
  register,
  errors,
  getOnFile,
  isChanged,
}: {
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors;
  getOnFile: () => string | null | undefined;
  isChanged: () => boolean;
}) {
  const onFile = getOnFile();
  return (
    <>
      <TextField
        {...register(DOB_FIELD)}
        type="date"
        label="Date of birth"
        margin="normal"
        fullWidth
        InputLabelProps={{ shrink: true }}
        helperText={
          (onFile ? `On file: ${onFile}` : "None on file") +
          " — a change is recorded in the account's age history with your account and the reason."
        }
      />
      <TextField
        {...register(DOB_REASON_FIELD, {
          validate: (value: string | undefined) =>
            !isChanged() ||
            (value ?? "").trim().length >= MIN_REASON_LENGTH ||
            `A reason of at least ${MIN_REASON_LENGTH} characters is required to change the date of birth`,
        })}
        error={!!errors?.[DOB_REASON_FIELD]}
        helperText={
          errors?.[DOB_REASON_FIELD]?.message?.toString() ??
          "Required when the date of birth changes — e.g. the support ticket and what was verified."
        }
        label="Reason for the date of birth change"
        margin="normal"
        fullWidth
        multiline
        minRows={2}
        InputLabelProps={{ shrink: true }}
      />
    </>
  );
}

/**
 * Post the corrected date. Rejects with the server's message (the data
 * provider's interceptor normalizes it), which the caller shows.
 */
export async function saveDateOfBirth(userId: number | string, dateOfBirth: string, reason: string) {
  await axiosInstance.post(`/api/v2/admin/users/${userId}/age-override`, {
    dateOfBirth,
    reason: reason.trim(),
  });
}
